import { Link, useParams, Navigate } from "react-router-dom";
import { Check, MapPin, MessageCircle, ArrowLeft, ArrowRight } from "lucide-react";
import { useLang } from "../i18n";
import { getService, SERVICES, waQuote } from "../data/site";
import Seo, { serviceLd, breadcrumbLd } from "../components/Seo";
import { Reveal, Kicker, MaskLine } from "../components/Reveal";
import ServiceCard from "../components/ServiceCard";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const prefix = lang === "ca" ? "/ca" : "";
  const service = getService(slug);
  if (!service) return <Navigate to={`${prefix}/servicios`} replace />;
  const d = service[lang] || service.es;
  const related = SERVICES.filter((s) => s.slug !== slug && !s.extraPage).slice(0, 3);

  return (
    <div data-testid={`service-page-${slug}`}>
      <Seo
        title={d.seoTitle}
        desc={d.seoDesc}
        path={`/servicios/${slug}`}
        image={service.img}
        jsonLd={[
          serviceLd(d.name, d.seoDesc, `${prefix}/servicios/${slug}`),
          breadcrumbLd([
            { name: "EcoGlow Detailing", path: prefix || "/" },
            { name: t("nav.services"), path: `${prefix}/servicios` },
            { name: d.name, path: `${prefix}/servicios/${slug}` },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="relative flex min-h-[70svh] items-end overflow-hidden pt-32">
        <img src={service.img} alt={d.h1} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-[#050505]/30" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-16 sm:px-6 lg:px-12">
          <Link to={`${prefix}/servicios`} data-testid="service-back-link" className="mb-6 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-[#8BFF32]">
            <ArrowLeft className="h-4 w-4" /> {t("servicePage.back")}
          </Link>
          <h1 className="max-w-3xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskLine delay={0.1}>{d.h1}</MaskLine>
          </h1>
          <Reveal delay={0.3}>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-zinc-300">{d.excerpt}</p>
          </Reveal>
          <Reveal delay={0.4}>
            <a
              href={waQuote(lang === "ca" ? `Hola EcoGlow! M'interessa el servei: ${d.name}.` : `¡Hola EcoGlow! Me interesa el servicio: ${d.name}.`)}
              target="_blank" rel="noopener noreferrer"
              data-testid="service-quote-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#63E51D] px-7 py-3.5 font-display text-lg font-bold uppercase tracking-wide text-black transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_25px_rgba(99,229,29,0.4)]"
            >
              <MessageCircle className="h-5 w-5" /> {t("servicesPage.cta")}
            </a>
          </Reveal>
        </div>
      </section>

      {/* Body */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
        <div className="grid gap-14 lg:grid-cols-5">
          <div className="space-y-6 lg:col-span-3">
            {d.body.map((p, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <p className="text-base leading-relaxed text-zinc-300">{p}</p>
              </Reveal>
            ))}
            {d.extras && (
              <div className="mt-10 grid gap-5 sm:grid-cols-2">
                {d.extras.map((ex, i) => (
                  <Reveal key={i} delay={i * 0.06}>
                    <div data-testid={`extra-card-${i}`} className="card-sheen flex h-full flex-col overflow-hidden rounded-xl border border-white/5 bg-[#121212] transition-all duration-300 hover:border-[#63E51D]/40">
                      <img src={ex.img} alt={ex.name} loading="lazy" className="aspect-[16/9] w-full object-cover" />
                      <div className="flex flex-1 flex-col p-5">
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="font-display text-xl font-bold uppercase tracking-wide text-white">{ex.name}</h3>
                          <span className="whitespace-nowrap rounded-full bg-[#63E51D]/10 px-3 py-1 font-mono2 text-xs font-bold text-[#8BFF32]">{ex.price}</span>
                        </div>
                        <p className="mt-2 flex-1 text-sm leading-relaxed text-zinc-400">{ex.desc}</p>
                        <a
                          href={waQuote(lang === "ca" ? `Hola EcoGlow! M'interessa: ${ex.name}.` : `¡Hola EcoGlow! Me interesa: ${ex.name}.`)}
                          target="_blank" rel="noopener noreferrer"
                          data-testid={`extra-quote-button-${i}`}
                          className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#63E51D] hover:text-[#8BFF32]"
                        >
                          <MessageCircle className="h-4 w-4" /> {t("servicesPage.cta")}
                        </a>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
          <div className="lg:col-span-2">
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-white/5 bg-[#121212] p-7">
                <h2 className="font-display text-xl font-bold uppercase tracking-wide text-[#8BFF32]">{t("servicePage.includes")}</h2>
                <ul className="mt-5 space-y-3">
                  {d.includes.map((inc, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-zinc-300">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#63E51D]" /> {inc}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-5 rounded-2xl border border-white/5 bg-[#121212] p-7">
                <h2 className="flex items-center gap-2 font-display text-xl font-bold uppercase tracking-wide text-[#8BFF32]">
                  <MapPin className="h-5 w-5" /> {t("servicePage.where")}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-zinc-300">{d.where}</p>
                <p className="mt-3 text-xs text-zinc-500">{t("servicePage.priceNote")}</p>
                <Link to={`${prefix}/packs`} data-testid="service-packs-link" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#63E51D] hover:text-[#8BFF32]">
                  {t("nav.packs")} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#080808] py-20">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <Reveal>
            <h2 className="font-display text-3xl font-black uppercase tracking-tight text-white sm:text-4xl">{t("servicePage.ctaTitle")}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-4 max-w-xl text-sm text-zinc-400">{t("servicePage.ctaText")}</p>
          </Reveal>
          <Reveal delay={0.2}>
            <a
              href={waQuote(lang === "ca" ? `Hola EcoGlow! M'interessa el servei: ${d.name}.` : `¡Hola EcoGlow! Me interesa el servicio: ${d.name}.`)}
              target="_blank" rel="noopener noreferrer"
              data-testid="service-bottom-quote-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#63E51D] px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-black transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_25px_rgba(99,229,29,0.4)]"
            >
              <MessageCircle className="h-5 w-5" /> {t("servicesPage.cta")}
            </a>
          </Reveal>
        </div>
      </section>

      {/* Related */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12">
        <Reveal>
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">{t("servicePage.related")}</h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
          {related.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.08}>
              <ServiceCard service={s} index={i} />
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
